"use client"

import * as React from "react"
import * as PopoverPrimitive from "@radix-ui/react-popover"

import { cn } from "@/lib/utils"

const Popover = PopoverPrimitive.Root

const PopoverTrigger = PopoverPrimitive.Trigger

const PopoverAnchor = PopoverPrimitive.Anchor

const PopoverContent = React.forwardRef<
  React.ElementRef<typeof PopoverPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof PopoverPrimitive.Content> & {
    /**
     * Portal the content to document.body. Default true — the right choice
     * everywhere except INSIDE A MODAL DIALOG.
     *
     * A Radix modal Dialog sets `body { pointer-events: none }` and traps focus
     * inside its own content, so anything portalled out to the body is left
     * unclickable and unfocusable. Passing `portal={false}` renders the content
     * in place, within the dialog's subtree, which restores interaction.
     * Positioning is unaffected: react-popper places the content with
     * `position: fixed`, so it is not clipped by a scrolling ancestor.
     */
    portal?: boolean
    /**
     * Where to portal to, instead of document.body. Pass a modal dialog's own
     * content element to get both: the content stays inside the dialog (so
     * its focus trap and pointer rules allow it) yet escapes the stacking
     * context of whatever opened it — a popover rendered in place inside a
     * sticky table cell was painted over by the cells of the rows below.
     */
    container?: HTMLElement | null
  }
>(({ className, align = "center", sideOffset = 4, portal = true, container, ...props }, ref) => {
  const content = (
    <PopoverPrimitive.Content
      ref={ref}
      align={align}
      sideOffset={sideOffset}
      className={cn(
        "z-50 w-72 rounded-md border bg-popover p-4 text-popover-foreground shadow-md outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    />
  )
  return portal ? (
    <PopoverPrimitive.Portal container={container ?? undefined}>{content}</PopoverPrimitive.Portal>
  ) : (
    content
  )
})
PopoverContent.displayName = PopoverPrimitive.Content.displayName

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor }
